import { cents, dollars, from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computedAsync } from '@vueuse/core';
import { ApplePay, PayPalCheckout } from 'braintree-web';
import dayjs from 'dayjs';
import { computed, MaybeRefOrGetter, ref, shallowRef, toValue } from 'vue';
import { Router } from 'vue-router';
import { Store } from 'vuex';

import { ShippingOffer } from '@/api/shippingCalculator';
import { createSession } from '@/api/shopify';
import { useRouteChange } from '@/composables/navigation/useRouteChange';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { useDelivery } from '@/composables/useDelivery';
import { useDiscount } from '@/composables/useDiscount';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import {
  applePayEnabled,
  applePayPaymentItem,
  PaymentItem,
  usePayment,
} from '@/composables/usePayment';
import money from '@/filters/money';
import { hasNonExpiredDefaultPaymentMethod } from '@/lib/checkout/payment';
import { useCustomer } from '@/stores/customer';
import {
  DeliveryMethodChangedEvent,
  PaymentCompleteEvent,
  ShippingAddressChangedEvent,
} from '@/types/shopify';
import { abbreviateStateIfNeeded, isValidStateOrProvince, NutsAddress } from '@/utils/address';
import { CheckoutEvents, formatPurchaseItem, gtag } from '@/utils/analytics';
import { sendCheckoutStartedEvent } from '@/utils/analytics/checkoutStartedEvent';
import { sendCheckoutStepCompletedEvent } from '@/utils/analytics/checkoutStepCompletedEvent';
import { generateRecaptcha } from '@/utils/captcha';
import { getCartId, isGiftCertificateLineItem, Shipment } from '@/utils/cart';
import { Money } from '@/utils/money';
import { reportError } from '@/utils/reportError';
import { retry } from '@/utils/retry';
import {
  buildPaymentRequest,
  buildShippingLinesPaymentRequest,
  convertOfferToDeliveryMethod,
} from '@/utils/shopify';

interface PayPalMoney {
  currency_code: 'USD';
  value: string;
}

interface PayPalPaymentItems {
  discount?: PayPalMoney;
  handling?: PayPalMoney;
  item_total?: PayPalMoney;
  shipping?: PayPalMoney;
  tax_total?: PayPalMoney;
}

export type ExpressLocation = 'Cart' | 'CartAdded' | 'HeaderCart' | 'Checkout';

function payPalPaymentItem(
  paymentItem: PaymentItem | { amount: Money; label: 'Nuts.com' },
): PayPalMoney {
  if (!paymentItem.amount) throw Error('only payment items with values are accepted');
  const value = Math.abs(dollars(paymentItem.amount)).toFixed(2);
  return { currency_code: 'USD', value };
}

function shippingOffersToApplePayShippingMethods(
  shippingOffers: ShippingOffer[],
): ApplePayJS.ApplePayShippingMethod[] {
  return shippingOffers.map((offer) => ({
    label: offer.name,
    amount: dollars(offer.price).toFixed(2),
    detail: dayjs(offer.earliestArrivalOn).format('ddd, MMMM D'),
    identifier: `${offer.carrier}:${offer.carrierCode}`,
  }));
}

function validateShippingAddress(address: NutsAddress) {
  return address.city && address.state && address.postalCode && address.country;
}

function formatPostalCodeIfNecessary(postalCode?: string) {
  return postalCode?.replace(/^(\d{5})(\d{4})?$/, (_, first, last) =>
    last ? `${first}-${last}` : first,
  );
}

function convertShipmentsToShippingCalculator(standardShipments: MaybeRefOrGetter<Shipment[]>) {
  const shipments = toValue(standardShipments);
  return shipments.slice(0, 1).map((shipment) => {
    if (shipment.address.country === 'US') {
      const postalCode = formatPostalCodeIfNecessary(shipment.address.postalCode);
      return {
        ...shipment,
        address: { ...shipment.address, postalCode },
      };
    }
    return shipment;
  });
}

export function useExpressCheckout(
  store: Store<any>,
  router?: Router,
  expressLocationSource?: MaybeRefOrGetter<ExpressLocation>,
) {
  const { cart, containsAutoDelivery, customLineItems, lineItems, setCart, setCustomerEmail } =
    useCart(store);
  const {
    cxMode,
    placeOrder,
    prepareCartForCheckout,
    purgeGiftOptions,
    resetCheckout,
    validateAndAuthorizeShopPay,
  } = useCheckout(store);
  const customerStore = useCustomer();
  const {
    applyShippingOffers,
    calculateShippingOffers,
    hasMultipleShipments,
    setShippingAddress,
    setShippingAddressCustomFieldsAfterPaymentAuthorized,
    standardShipments,
  } = useDelivery(store);
  const { estimatedAmountToCharge, getSavedPaymentMethods, offerThirdPartyPayments, paymentItems } =
    usePayment(store, cxMode);
  const { appliedDiscountCodes } = useDiscount(store);
  const { navigateTo } = useRouteChange(router);
  const { flags, loadDyFlags } = useFeatureFlags();

  const evaluatingShopPay = shallowRef(false);

  const offerExpressCheckout = computed<boolean>(() => {
    if (containsAutoDelivery.value && (!customerStore.customer || cxMode.value)) {
      return false;
    }
    if (customerStore.storeCredit?.balance && cents(customerStore.storeCredit.balance)) {
      return false;
    }
    if (lineItems.value.some(isGiftCertificateLineItem)) {
      return false;
    }
    return lineItems.value.length > 0 && offerThirdPartyPayments.value;
  });

  /**
   * Determines if ShopPay is eligible as an express payment method.
   *
   * If the cart contains auto-delivery items, this triggers an HTTP call, so use this cautiously.
   */
  const offerShopPay = computedAsync(
    async () => {
      // Templates might invoke this during SSR but we only want this to load on client side
      if (typeof window === 'undefined') return false;

      await loadDyFlags('[A/B Test] Shop Pay', ['supportShopPay']);

      if (!flags.supportShopPay) return false;
      if (!offerExpressCheckout.value) return false;
      if (!containsAutoDelivery.value) return true;

      const paymentMethods = await getSavedPaymentMethods();
      return paymentMethods.length > 0 && hasNonExpiredDefaultPaymentMethod(paymentMethods);
    },
    false,
    { evaluating: evaluatingShopPay, lazy: true },
  );

  const expressLocation = computed(() => toValue(expressLocationSource));

  const offerApplePay = computed<boolean>(() => {
    if (!offerExpressCheckout.value) return false;
    return applePayEnabled();
  });

  const showErrorIfOfferNotAvailable = (session: ApplePaySession, shippingAddress: NutsAddress) => {
    const err = Error('invalid shipping method selected');
    reportError(
      err,
      `Shipping method no longer available for Apple Pay. Apple Pay address: ${JSON.stringify(
        shippingAddress,
      )}`,
    );
    session.abort();
    // eslint-disable-next-line no-alert
    alert('Shipping method no longer available for Apple Pay');
    return err;
  };

  const calculateShippingOfferWithRetry = async (
    requestShipments: Shipment[],
  ): Promise<ShippingOffer[]> =>
    retry(
      async () => {
        const {
          offerSets: [{ shippingOffers: offers }],
        } = await calculateShippingOffers({
          requestShipments,
          regionalCarriersAllowed: customerStore.customerRegionalCarriersAllowed,
          ontracAllowed: customerStore.customerOntracAllowed,
          lasershipAllowed: customerStore.customerLasershipAllowed,
        });
        return offers;
      },
      {
        onError: async (err) => {
          reportError(
            err,
            `Error calling shipping calculator - Status: ${err.response?.status}, Message: ${err.message}`,
          );
          if (
            !err.response?.status ||
            err.response?.status === 502 ||
            err.message === 'Network Error'
          ) {
            await new Promise((resolve) => {
              setTimeout(resolve, 1000);
            });
            return true;
          }
          return false;
        },
      },
    );

  const applyShippingOffer = async (shippingOptionId: string, shippingOffers?: ShippingOffer[]) => {
    const shippingMethod = shippingOffers?.find(
      (offer: ShippingOffer) => `${offer.carrier}:${offer.carrierCode}` === shippingOptionId,
    );
    if (!shippingMethod) throw Error('invalid shipping method selected');

    const [{ key }] = standardShipments.value;
    const addHeatResistantPacking =
      shippingMethod.containsMeltables || shippingMethod.heatResistantIncluded;
    await applyShippingOffers([
      {
        key,
        offer: shippingMethod,
        addHeatResistantPacking,
      },
    ]);
  };

  const addressChanged = (shippingAddress: NutsAddress) => {
    if (!standardShipments.value.length) return true;
    const stringify = (address: NutsAddress) =>
      `${address.city} ${address.state} ${address.postalCode}`;
    return stringify(shippingAddress) !== stringify(standardShipments.value[0].address);
  };

  return {
    offerApplePay,
    offerExpressCheckout,
    offerShopPay,

    async requestApplePayPayment(applePay?: ApplePay, deviceData?: string): Promise<void> {
      const checkoutEvent = computed(() => ({
        checkout_type: 'Apple Pay',
        currency: 'USD',
        value: money(estimatedAmountToCharge.value),
        coupon: appliedDiscountCodes.value[0]?.code as string | undefined,
        items: lineItems.value.map(formatPurchaseItem),
      }));

      sendCheckoutStartedEvent({
        expressCheckoutMethod: 'Apple Pay',
      });

      gtag('event', 'begin_checkout', checkoutEvent.value);
      if (!applePay) throw Error('Missing required Apple Pay instance!');

      const merchantName = 'Nuts.com';

      const shippingOffers = ref<ShippingOffer[]>();
      const shippingOffer = ref<ShippingOffer>();

      const paymentLineItems = computed(() =>
        paymentItems.value
          .filter((p) => p.amount !== undefined)
          .map((p) => applePayPaymentItem(p, 'final')),
      );

      const total = computed(() =>
        applePayPaymentItem({
          amount: estimatedAmountToCharge.value,
          label: merchantName,
        }),
      );

      type BraintreeApplePayConfig = Pick<
        ApplePayJS.ApplePayPaymentRequest,
        'merchantCapabilities' | 'supportedNetworks'
      >;
      // @ts-ignore (@types/braintree-web is wrong and exposes a Partial<ApplePayPaymentRequest>)
      const braintreeConfig: BraintreeApplePayConfig = applePay.createPaymentRequest({});
      const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
        ...braintreeConfig,
        countryCode: 'US',
        currencyCode: 'USD',
        lineItems: paymentLineItems.value,
        requiredBillingContactFields: ['postalAddress', 'name'],
        requiredShippingContactFields: ['postalAddress', 'phone', 'email', 'name'],
        total: total.value,
      };

      const session = new ApplePaySession(3, paymentRequest);

      await prepareCartForCheckout();
      if (hasMultipleShipments.value) await resetCheckout();
      else await purgeGiftOptions();

      // Merchant Validation
      session.onvalidatemerchant = async (event) => {
        try {
          // @ts-ignore (@types/braintree-web is wrong and works with promises)
          const merchantSession = await applePay.performValidation({
            validationURL: event.validationURL,
            displayName: merchantName,
          });
          session.completeMerchantValidation(merchantSession);
        } catch (err) {
          reportError(err, 'Apple Pay failed to load');
          // eslint-disable-next-line no-alert
          alert('Apple Pay failed to load');
        }
      };

      // Shipping Contact Selection
      session.onshippingcontactselected = async (event) => {
        let errors: ApplePayJS.ApplePayError[] | undefined;
        let newShippingMethods: ApplePayJS.ApplePayShippingMethod[] = [];
        const { shippingContact } = event;

        try {
          const shippingAddress = NutsAddress.fromApplePay(shippingContact);

          if (!validateShippingAddress(shippingAddress)) throw Error('invalid address');

          await setShippingAddress(shippingAddress);

          const requestShipments = standardShipments.value.slice(0, 1);
          const {
            offerSets: [{ key, shippingOffers: offers }],
          } = await calculateShippingOffers({
            requestShipments,
            regionalCarriersAllowed: customerStore.customerRegionalCarriersAllowed,
            ontracAllowed: true,
          });
          shippingOffers.value = offers;
          const addHeatResistantPacking =
            offers[0].containsMeltables || offers[0].heatResistantIncluded;
          await applyShippingOffers([
            {
              key,
              offer: offers[0],
              addHeatResistantPacking,
            },
          ]);
          newShippingMethods = shippingOffersToApplePayShippingMethods(offers);
        } catch (err) {
          errors = [
            new ApplePayError(
              'addressUnserviceable',
              'addressLines',
              'Cannot ship to this location',
            ),
          ];
        }
        if (!errors) {
          const eventProps = {
            checkout_id: getCartId(cart.value),
            checkoutType: checkoutEvent.value.checkout_type,
            coupon: checkoutEvent.value.coupon,
            lineItems: lineItems.value,
            paymentMethod: 'Apple Pay',
            shipping_option: standardShipments.value[0]?.customFields?.shipmentPickupShippingOption,
            total: estimatedAmountToCharge.value,
          };
          // technically, both checkout steps are completed by this point
          sendCheckoutStepCompletedEvent({ ...eventProps, step: 'address' });
          sendCheckoutStepCompletedEvent({ ...eventProps, step: 'delivery' });
        }

        session.completeShippingContactSelection({
          errors,
          newLineItems: paymentLineItems.value,
          newShippingMethods,
          newTotal: total.value,
        });
      };

      // Shipping Method Selection
      session.onshippingmethodselected = async (event) => {
        const { identifier } = event.shippingMethod;
        const shippingMethod = shippingOffers.value?.find(
          (o) => `${o.carrier}:${o.carrierCode}` === identifier,
        );
        if (shippingMethod) {
          shippingOffer.value = shippingMethod;
          const [{ key }] = standardShipments.value;
          const addHeatResistantPacking =
            shippingMethod.containsMeltables || shippingMethod.heatResistantIncluded;
          await applyShippingOffers([
            {
              key,
              offer: shippingMethod,
              addHeatResistantPacking,
            },
          ]);
        }
        session.completeShippingMethodSelection({
          newLineItems: paymentLineItems.value,
          newTotal: total.value,
        });
      };

      // Payment Authorized
      session.onpaymentauthorized = async (event) => {
        let nonce: string | undefined;
        try {
          // @ts-ignore (@types/braintree-web is wrong; now takes an object and handle promises)
          ({ nonce } = await applePay.tokenize({ token: event.payment.token }));
        } catch (err) {
          const label = reportError(err, 'Apple Pay failed to tokenize');
          CheckoutEvents.event({ action: 'Express Checkout', label, category: 'Checkout Error' });
          session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
          return;
        }
        const maybeAddress = (contactAddress?: ApplePayJS.ApplePayPaymentContact) => {
          if (!contactAddress) return undefined;
          return NutsAddress.fromApplePay(contactAddress);
        };

        const shippingAddress = maybeAddress(event.payment.shippingContact);
        try {
          if (!shippingAddress) throw new ApplePayError('shippingContactInvalid');
          if (!shippingAddress.street1?.trim().length)
            throw new ApplePayError('shippingContactInvalid', 'addressLines');
          if (!shippingAddress.city?.trim().length)
            throw new ApplePayError('shippingContactInvalid', 'locality');
          if (!shippingAddress.state?.trim().length)
            throw new ApplePayError('shippingContactInvalid', 'administrativeArea');
          if (!shippingAddress.country?.trim().length)
            throw new ApplePayError('shippingContactInvalid', 'country');
          await setCustomerEmail(shippingAddress.email);

          const addressState = abbreviateStateIfNeeded(shippingAddress);
          await setShippingAddress({
            ...standardShipments.value[0].address,
            ...shippingAddress,
            state: addressState,
          });

          if (!isValidStateOrProvince(shippingAddress.state, shippingAddress.country))
            throw new ApplePayError('shippingContactInvalid', 'administrativeArea');

          if (!shippingOffer.value) {
            throw showErrorIfOfferNotAvailable(session, shippingAddress);
          }
          await setShippingAddressCustomFieldsAfterPaymentAuthorized(
            shippingAddress,
            'shipment-1',
            shippingOffer.value,
          );
        } catch (err) {
          let errors: ApplePayJS.ApplePayError[] | undefined;
          if (err instanceof ApplePayError) {
            errors = [err as ApplePayJS.ApplePayError];
          }
          const label = reportError(err, 'Apple Pay unable to finalize shipping address');
          CheckoutEvents.event({ action: 'Express Checkout', label, category: 'Checkout Error' });
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors,
          });
          // eslint-disable-next-line no-alert
          alert('Failed to finalize order');
          return;
        }

        const saveToWallet = containsAutoDelivery.value;
        let setAsDefaultInWallet = false;
        if (containsAutoDelivery.value) {
          try {
            const paymentMethods = await getSavedPaymentMethods();
            const hasDefaultMethod = paymentMethods.some((m) => m.default);
            setAsDefaultInWallet = saveToWallet && !hasDefaultMethod;
          } catch (err) {
            const label = reportError(err, 'Apple Pay unable to get wallet for Auto Delivery user');
            CheckoutEvents.event({ action: 'Express Checkout', label, category: 'Checkout Error' });
            session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
            // eslint-disable-next-line no-alert
            alert('Failed to finalize order');
            return;
          }
        }

        try {
          const captchaResponse = await generateRecaptcha();
          const { order, receiptToken } = await placeOrder({
            billingAddress: {
              ...NutsAddress.fromApplePay(event.payment.billingContact ?? {}),
              email: shippingAddress.email,
            },
            deviceData,
            'g-recaptcha-response': captchaResponse,
            saveToWallet,
            setAsDefaultInWallet,
            paymentNonces: [`apple_pay_card:${nonce}`],
          });

          gtag('event', 'add_payment_info', {
            ...checkoutEvent.value,
            payment_type: order?.paymentInfo?.payments[0]?.obj?.paymentMethodInfo?.name?.en,
          });

          CheckoutEvents.purchase({
            expressLocation: expressLocation.value,
            order,
            paymentItems: paymentItems.value,
            paymentOption: 'ApplePay',
            receiptToken,
          });

          session.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
          if (router) window.scrollTo({ top: 0, behavior: 'smooth' });
          navigateTo(`/checkout/receipt?token=${receiptToken}`);
        } catch (err) {
          const label = reportError(err, 'Apple Pay failed to finalize order');
          CheckoutEvents.event({ action: 'Express Checkout', label, category: 'Checkout Error' });
          session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
          session.abort();
          // eslint-disable-next-line no-alert
          alert('Apple Pay failed to finalize order');
        }
      };

      return session.begin();
    },
    async requestShopPayPayment() {
      const orderPlaced = ref(false);
      const shippingOffers = ref<ShippingOffer[]>();
      const shopPayEmail = ref<string>();

      const paymentRequest = buildPaymentRequest(cart.value, lineItems.value, [], true);
      const session = window.ShopPay?.PaymentRequest.createSession({
        paymentRequest,
      });

      if (!session) {
        if (window.ShopPay) {
          const error = new Error('Failed to create session due to unknown reasons');
          reportError(error, JSON.stringify(paymentRequest));
          throw error;
        } else {
          const webComponentCompatibleBrowser = Boolean(window.customElements);
          if (webComponentCompatibleBrowser) {
            const error = new Error('ShopPay is not defined due to browser compatibility issues');
            reportError(error, JSON.stringify(paymentRequest));
            throw error;
          } else {
            const error = new Error('ShopPay is not defined for unknown reasons');
            reportError(error, JSON.stringify(paymentRequest));
            throw error;
          }
        }
      }

      function handleShopPayError(error: any, message: string) {
        orderPlaced.value = false;
        const label = reportError(error, message);
        CheckoutEvents.event({ action: 'Express Checkout', label, category: 'Checkout Error' });

        session?.completePaymentConfirmationRequest({
          errors: [{ type: 'generalError', message: 'Something went wrong. Please try again.' }],
        });
      }

      session.addEventListener('sessionrequested', async () => {
        sendCheckoutStartedEvent({
          expressCheckoutMethod: 'Shop Pay',
        });

        await resetCheckout();
        await prepareCartForCheckout();
        shippingOffers.value = undefined;
        const updatedPaymentRequest = window.ShopPay?.PaymentRequest.build(
          buildPaymentRequest(cart.value, lineItems.value, [], true),
        );
        const response = await createSession(updatedPaymentRequest, cart.value!.id);
        const { token, checkoutUrl, sourceIdentifier } = response.shopPayPaymentRequestSession;
        session.completeSessionRequest({
          token,
          checkoutUrl,
          sourceIdentifier,
          updatedPaymentRequest,
        });
      });

      session.addEventListener(
        'shippingaddresschanged',
        async (ev: ShippingAddressChangedEvent) => {
          const selectedAddress = ev.shippingAddress;

          if (!selectedAddress) {
            await resetCheckout();
            shippingOffers.value = [];
            const updatedPaymentRequest = buildPaymentRequest(
              cart.value,
              lineItems.value,
              [],
              true,
            );
            session.completeShippingAddressChange({
              updatedPaymentRequest,
            });
            return;
          }

          if (selectedAddress.email && cart.value?.anonymousId) {
            await setCustomerEmail(selectedAddress.email?.toLowerCase());
          }
          shopPayEmail.value = selectedAddress.email;

          const shippingAddress = NutsAddress.fromShopPay(selectedAddress);
          const needShippingOffers = addressChanged(shippingAddress) || !shippingOffers.value;
          await setShippingAddress(shippingAddress);

          let shippingOffersFound = false;
          let shippingCalculatorAnswered = false;
          let shippingCalculatorCalled = false;
          try {
            if (needShippingOffers) {
              const requestShipments = convertShipmentsToShippingCalculator(standardShipments);
              shippingCalculatorCalled = true;
              const offers = await calculateShippingOfferWithRetry(requestShipments);
              shippingCalculatorAnswered = true;
              shippingOffersFound = !!offers.length;
              shippingOffers.value = offers;
              const identifier = `${offers[0].carrier}:${offers[0].carrierCode}`;
              await applyShippingOffer(identifier!, shippingOffers.value);
            } else {
              const firstOffer = shippingOffers.value ? shippingOffers.value[0] : undefined;
              await applyShippingOffer(
                `${firstOffer?.carrier}:${firstOffer?.carrierCode}`,
                shippingOffers.value,
              );
            }
          } catch (err) {
            const shippingError =
              (!shippingOffersFound && shippingCalculatorAnswered) ||
              (shippingCalculatorCalled && !shippingCalculatorAnswered);
            const message = shippingError
              ? 'Please update your address and try again. If the issue persists, please contact our customer experience team.'
              : 'Please try again. If the issue persists, please contact our customer experience team.';
            const errorMessage = [
              `Shop Pay Express: ${message}`,
              `Details: ${JSON.stringify({
                address: selectedAddress,
                needShippingOffers,
                shippingOffersFound,
                shippingCalculatorAnswered,
              })}`,
              `Error: ${err instanceof Error ? err.constructor.name : 'Unknown error'}`,
            ].join(' ');
            reportError(err, errorMessage);
            session.completeShippingAddressChange({
              errors: [
                {
                  type: 'shippingAddressError',
                  message,
                },
              ],
            });
            return;
          }
          const firstOffer = shippingOffers.value ? shippingOffers.value[0] : undefined;
          const currencyCode = cart.value?.totalPrice.currencyCode ?? 'USD';

          const hrpItems = cart.value?.customLineItems?.filter((item) =>
            item.slug.endsWith('-heat-resistant-packaging'),
          );

          const selectedDeliveryMethod = convertOfferToDeliveryMethod(firstOffer, hrpItems);
          const updatedPaymentRequest = window.ShopPay?.PaymentRequest.build({
            ...buildPaymentRequest(cart.value, lineItems.value, shippingOffers.value),
            ...buildShippingLinesPaymentRequest(selectedDeliveryMethod, currencyCode),
          });
          session.completeShippingAddressChange({ updatedPaymentRequest });
        },
      );

      session.addEventListener('deliverymethodchanged', async (ev: DeliveryMethodChangedEvent) => {
        const currentPaymentRequest = session.paymentRequest;
        const selectedDeliveryMethod = ev.deliveryMethod;
        if (!selectedDeliveryMethod) {
          session.completeDeliveryMethodChange({
            updatedPaymentRequest: currentPaymentRequest,
          });
          return;
        }

        await applyShippingOffer(selectedDeliveryMethod.code, shippingOffers.value);

        const currencyCode = cart.value?.totalPrice.currencyCode ?? 'USD';
        const updatedRequestValues = {
          ...buildPaymentRequest(cart.value, lineItems.value, shippingOffers.value),
          ...buildShippingLinesPaymentRequest(selectedDeliveryMethod, currencyCode),
        };

        const updatedPaymentRequest = window.ShopPay?.PaymentRequest.build(updatedRequestValues);
        session.completeDeliveryMethodChange({ updatedPaymentRequest });
      });

      session.addEventListener('paymentconfirmationrequested', async () => {
        if (!session.paymentRequest || !session.token || !session.sourceIdentifier) {
          handleShopPayError(
            undefined,
            'Shop Pay failed to process payment confirmation because of missing params.',
          );
          return;
        }

        const captcha = await generateRecaptcha();

        try {
          orderPlaced.value = true;

          const response = await validateAndAuthorizeShopPay({
            'g-recaptcha-response': captcha,
            paymentNonces: [`shop_pay:${session.token}`],
            shopPayPlaceOrderRequest: {
              paymentRequest: session.paymentRequest,
              sourceIdentifier: session.sourceIdentifier,
            },
          });

          const {
            cart: updatedCart,
            shopPayPaymentRequestSessionSubmit: { paymentRequestReceipt, userErrors },
          } = response;

          setCart(updatedCart);

          if (paymentRequestReceipt?.processingStatusType === 'ready') {
            session.completePaymentConfirmationRequest();
          } else {
            handleShopPayError(userErrors, 'Shop Pay failed during payment confirmation.');
          }
        } catch (err) {
          handleShopPayError(err, 'Shop Pay failed during payment confirmation.');
        }
      });

      session.addEventListener('paymentcomplete', async (ev: PaymentCompleteEvent) => {
        if (ev.processingStatus.status !== 'completed') {
          orderPlaced.value = false;
          return;
        }

        const captcha = await generateRecaptcha();

        try {
          const { order, receiptToken } = await placeOrder({
            billingAddress: NutsAddress.fromShopPay(ev.processingStatus.billingAddress),
            'g-recaptcha-response': captcha,
            paymentNonces: [`shop_pay:${session.token}`],
            shopPayAction: 'place-order',
            shopPayPlaceOrderRequest: {
              completedAt: ev.processingStatus.completedAt,
              creditCardDetails: {
                brand: ev.processingStatus.creditCardDetails.brand,
                lastDigits: ev.processingStatus.creditCardDetails.lastDigits,
              },
              shopPayEmail: shopPayEmail.value,
            },
          });

          CheckoutEvents.purchase({
            expressLocation: expressLocation.value,
            order,
            paymentItems: paymentItems.value,
            paymentOption: 'ShopPay',
            receiptToken,
          });
          session.close();
          navigateTo(`/checkout/receipt?token=${receiptToken}`);
        } catch (err) {
          orderPlaced.value = false;
          const label = reportError(err, 'Shop Pay failed to finalize order');
          CheckoutEvents.event({ action: 'Express Checkout', label, category: 'Checkout Error' });
          session.close();
          // eslint-disable-next-line no-alert
          alert(
            'Failed to finalize order. Please try again or contact our customer experience team.',
          );
        }
      });

      session.addEventListener('windowclosed', async () => {
        if (!orderPlaced.value) {
          await resetCheckout();
        }
      });

      if (window.ShopPay) {
        window.ShopPay.session = session;
      }
    },

    requestPayPalCheckout(
      paypalCheckout: PayPalCheckout,
      deviceData?: string,
    ): Parameters<typeof window.paypal.Buttons>[0] {
      let identifier: string | undefined;
      const shippingOffers = ref<ShippingOffer[]>();

      const surchargeLineItem = computed(() =>
        customLineItems.value.find((l) => l.slug === 'surcharge'),
      );
      const adjustedSubTotal = computed(() => {
        const subTotal = paymentItems.value.find((i) => i.label === 'Subtotal')!;
        return {
          ...subTotal,
          amount: Money.add(subTotal.amount!, surchargeLineItem.value?.money ?? from(0)),
        };
      });

      const paymentLineItems = computed(() => {
        const keys: Record<string, keyof PayPalPaymentItems> = {
          Discount: 'discount',
          Subtotal: 'item_total',
          Shipping: 'shipping',
          Tax: 'tax_total',
        };

        return paymentItems.value.reduce<PayPalPaymentItems>((breakdown, item) => {
          if (item.amount === undefined) return breakdown;

          switch (item.label) {
            case 'Subtotal':
              return { ...breakdown, item_total: payPalPaymentItem(adjustedSubTotal.value) };

            case 'Shipping': {
              let shippingAmount = item.amount;

              const hrpItem = paymentItems.value.find(
                (i) => i.label === 'Heat-Resistant Packaging' && i.amount !== undefined,
              );

              if (hrpItem?.amount && cents(shippingAmount) && cents(hrpItem.amount)) {
                shippingAmount = Money.add(shippingAmount, hrpItem.amount);
              }
              return {
                ...breakdown,
                shipping: payPalPaymentItem({ ...item, amount: shippingAmount }),
              };
            }

            default:
              if (item.label in keys) {
                return { ...breakdown, [keys[item.label]]: payPalPaymentItem(item) };
              }
              return breakdown;
          }
        }, {});
      });

      const checkoutEvent = computed(() => ({
        checkout_type: 'Paypal',
        currency: 'USD',
        value: money(estimatedAmountToCharge.value),
        coupon: appliedDiscountCodes.value[0]?.code as string | undefined,
        items: lineItems.value.map(formatPurchaseItem),
      }));

      return {
        fundingSource: 'paypal',
        async createOrder() {
          gtag('event', 'begin_checkout', checkoutEvent.value);

          sendCheckoutStartedEvent({
            expressCheckoutMethod: 'PayPal',
          });

          await resetCheckout();
          await prepareCartForCheckout();

          const payPalLineItems = lineItems.value.map((lineItem) => ({
            description: undefined,
            kind: 'debit' as const as paypal.LineItemKind,
            name: lineItem.name.en,
            productCode: undefined,
            quantity: String(lineItem.quantity),
            unitAmount: dollars(lineItem.piecePrice).toFixed(2),
            unitTaxAmount: undefined,
            url: undefined,
          }));
          if (surchargeLineItem.value) {
            payPalLineItems.push({
              description: undefined,
              kind: 'debit' as const as paypal.LineItemKind,
              name: paymentItems.value.find((i) => i.label.includes('Surcharge'))!.label,
              productCode: undefined,
              quantity: String(surchargeLineItem.value.quantity),
              unitAmount: dollars(surchargeLineItem.value.money).toFixed(2),
              unitTaxAmount: undefined,
              url: undefined,
            });
          }

          return paypalCheckout.createPayment({
            amount: dollars(adjustedSubTotal.value.amount),
            currency: 'USD',
            enableShippingAddress: true,
            flow: 'checkout' as const as typeof window.paypal.FlowType.Checkout,
            intent: 'authorize' as const as typeof window.paypal.Intent.Authorize,
            lineItems: payPalLineItems,
            requestBillingAgreement: true,
            shippingAddressEditable: true,
          });
        },
        async onApprove(data) {
          const payload = await paypalCheckout.tokenizePayment(data);
          try {
            await setCustomerEmail(payload.details.email);
            if (payload.details.shippingAddress) {
              const phone = payload.details.shippingAddress.phone ?? payload.details.phone;
              let nutsAddress = NutsAddress.fromPayPal(payload.details.shippingAddress);
              if (nutsAddress.country === 'US') {
                const postalCode = formatPostalCodeIfNecessary(nutsAddress.postalCode);
                nutsAddress = { ...nutsAddress, postalCode };
              }
              const address = {
                ...standardShipments.value[0].address,
                ...nutsAddress,
                phone,
              };
              await setShippingAddress(address);
              await applyShippingOffer(identifier!, shippingOffers.value);
            }
            const captcha = await generateRecaptcha();

            let saveToWallet = false;
            if (containsAutoDelivery.value) {
              const savedPaymentMethods = await getSavedPaymentMethods();
              const hasDefaultPaymentMethod =
                savedPaymentMethods.length &&
                hasNonExpiredDefaultPaymentMethod(savedPaymentMethods);
              saveToWallet = !hasDefaultPaymentMethod && containsAutoDelivery.value;
            }

            const { order, receiptToken } = await placeOrder({
              deviceData,
              paymentNonces: [`paypal_account:${payload.nonce}`],
              'g-recaptcha-response': captcha,
              saveToWallet,
              setAsDefaultInWallet: saveToWallet,
            });

            gtag('event', 'add_payment_info', {
              ...checkoutEvent.value,
              payment_type: order.paymentInfo?.payments[0]?.obj?.paymentMethodInfo?.name?.en,
            });

            CheckoutEvents.purchase({
              expressLocation: expressLocation.value,
              order,
              paymentItems: paymentItems.value,
              paymentOption: 'PayPal',
              receiptToken,
            });

            if (router) window.scrollTo({ top: 0, behavior: 'smooth' });
            navigateTo(`/checkout/receipt?token=${receiptToken}`);
          } catch (err) {
            const label = reportError(err, 'PayPal failed to finalize order');
            CheckoutEvents.event({ action: 'Express Checkout', label, category: 'Checkout Error' });

            let errorMessage = 'PayPal failed to finalize order';
            if (typeof err === 'string') {
              errorMessage = err;
            } else if (err instanceof Error) {
              errorMessage = err.message;
            }
            // eslint-disable-next-line no-alert
            alert(errorMessage);
          }
        },
        onError(error) {
          reportError(error, 'PayPal Express Checkout Error');
        },
        async onShippingChange(data, actions) {
          let shippingAddress = NutsAddress.fromPayPal(data.shipping_address);
          if (shippingAddress.country === 'US') {
            const postalCode = formatPostalCodeIfNecessary(shippingAddress.postalCode);
            shippingAddress = { ...shippingAddress, postalCode };
          }
          const needShippingOffers =
            !data.selected_shipping_option || addressChanged(shippingAddress);

          await setShippingAddress(shippingAddress);

          let shippingOption;
          identifier = data?.selected_shipping_option?.id;
          let shippingOffersFound = false;
          let shippingCalculatorAnswered = false;
          try {
            if (needShippingOffers) {
              const requestShipments = convertShipmentsToShippingCalculator(standardShipments);
              const offers = await calculateShippingOfferWithRetry(requestShipments);
              shippingCalculatorAnswered = true;
              shippingOffersFound = !!offers.length;
              shippingOffers.value = offers;
              identifier = `${offers[0].carrier}:${offers[0].carrierCode}`;
              shippingOption = offers[0].name;
            } else {
              shippingOption = data?.selected_shipping_option?.label;
            }

            await applyShippingOffer(identifier!, shippingOffers.value);
          } catch (err) {
            const message =
              !shippingOffersFound && shippingCalculatorAnswered
                ? 'Please update your address and try again. If the issue persists, complete your purchase using the standard checkout option.'
                : 'Please try again. If the issue persists, complete your purchase using the standard checkout option.';
            const errorMessage = [
              `PayPal Express: ${message}`,
              `Details: ${JSON.stringify({
                address: data.shipping_address,
                cartId: cart.value?.id,
                needShippingOffers,
                identifier,
                shippingOption,
                shippingOffersFound,
                shippingCalculatorAnswered,
                paymentToken: data.paymentToken,
              })}`,
              `Error: ${err instanceof Error ? err.constructor.name : 'Unknown error'}`,
            ].join(' ');

            reportError(err, errorMessage);
            actions.reject();
            // eslint-disable-next-line no-alert
            alert(message);
            throw err;
          }

          const eventProps = {
            checkout_id: getCartId(cart.value),
            checkoutType: checkoutEvent.value.checkout_type,
            coupon: checkoutEvent.value.coupon,
            lineItems: lineItems.value,
            paymentMethod: 'PayPal',
            shipping_option: shippingOption,
            total: estimatedAmountToCharge.value,
          };
          // technically, both checkout steps are completed by this point
          sendCheckoutStepCompletedEvent({ ...eventProps, step: 'address' });
          sendCheckoutStepCompletedEvent({ ...eventProps, step: 'delivery' });

          return actions.order.patch([
            {
              op: 'replace',
              path: "/purchase_units/@reference_id=='default'/amount",
              value: {
                currency_code: 'USD',
                value: dollars(estimatedAmountToCharge.value).toFixed(2),
                breakdown: paymentLineItems.value,
              },
            },
            {
              op: data.selected_shipping_option ? 'replace' : 'add',
              path: "/purchase_units/@reference_id=='default'/shipping/options",
              value:
                shippingOffers.value?.map((offer) => {
                  let label = offer.name;
                  let { price } = offer;

                  const hrp = paymentItems.value.find(
                    (i) => i.label === 'Heat-Resistant Packaging',
                  );
                  if (hrp?.amount && cents(hrp.amount)) {
                    label += ` + ${hrp.label}`;
                    price = Money.add(price, hrp.amount);
                  }

                  return {
                    amount: { currency_code: 'USD', value: dollars(price).toFixed(2) },
                    id: `${offer.carrier}:${offer.carrierCode}`,
                    label,
                    selected: `${offer.carrier}:${offer.carrierCode}` === identifier,
                    type: 'SHIPPING',
                  };
                }) ?? [],
            },
          ]);
        },
      };
    },
  };
}
